import { useCallback, useState } from 'react';

import { useApolloClient } from '@apollo/client/react';
import { Apollo } from 'src/gql/generated';
import { Sentry } from 'src/sentry';

type FilestackDownloadFailureReason = 'expired-policy' | 'http-error' | 'mime-mismatch';

export class FilestackDownloadError extends Error {
  readonly status: number | null;
  readonly reason: FilestackDownloadFailureReason;

  constructor(
    message: string,
    options: { reason: FilestackDownloadFailureReason; status?: number | null }
  ) {
    super(message);
    this.name = 'FilestackDownloadError';
    this.reason = options.reason;
    this.status = options.status ?? null;
  }
}

interface FetchFilestackBlobOptions {
  /** Reject responses whose MIME type doesn't start with this prefix (e.g. 'image/'). */
  expectedMimePrefix?: string;
}

/**
 * Fetch a signed Filestack URL and validate the response. Once a signed URL's policy expires,
 * Filestack returns a small text error body (e.g. "policy error: the signature has expired")
 * instead of file bytes, so callers must never trust a bare fetch().blob().
 */
export async function fetchFilestackBlob(
  url: string,
  options: FetchFilestackBlobOptions = {}
): Promise<Blob> {
  const { expectedMimePrefix } = options;
  const response = await fetch(url);

  if (!response.ok) {
    const bodySnippet = await getResponseBodySnippet(response);
    const isExpiredPolicy = /policy|signature/i.test(bodySnippet);
    throw new FilestackDownloadError(
      `Filestack download failed with HTTP ${response.status}: ${bodySnippet}`,
      { reason: isExpiredPolicy ? 'expired-policy' : 'http-error', status: response.status }
    );
  }

  const blob = await response.blob();
  if (expectedMimePrefix && !blob.type.startsWith(expectedMimePrefix)) {
    throw new FilestackDownloadError(
      `Expected Filestack response of type ${expectedMimePrefix}*, received ${blob.type || 'unknown'}.`,
      { reason: 'mime-mismatch', status: response.status }
    );
  }

  return blob;
}

interface FetchFreshFilestackBlobOptions extends FetchFilestackBlobOptions {
  /** Mints a fresh signed URL after the first fetch fails (e.g. expired policy). */
  refreshUrl: () => Promise<string | null | undefined>;
  url: string;
  /** Extra Sentry context, e.g. the DocFile id and the calling feature. */
  sentryContext?: Record<string, unknown>;
}

/**
 * Fetch a signed Filestack URL, and if the response is invalid (expired policy, HTTP error, or
 * unexpected MIME type), mint a fresh signed URL and retry once. Signed URLs can sit in the
 * Apollo cache or component state for longer than their 4-hour policy window, so the first
 * attempt may use a stale URL.
 */
export async function fetchFreshFilestackBlob(
  options: FetchFreshFilestackBlobOptions
): Promise<Blob> {
  const { refreshUrl, sentryContext, url, ...fetchOptions } = options;

  try {
    return await fetchFilestackBlob(url, fetchOptions);
  } catch (staleError) {
    const freshUrl = await refreshUrl().catch(() => null);
    if (!freshUrl) {
      captureFilestackDownloadError(staleError, { ...sentryContext, recovered: false });
      throw staleError;
    }

    try {
      const blob = await fetchFilestackBlob(freshUrl, fetchOptions);
      captureFilestackDownloadError(staleError, { ...sentryContext, recovered: true });
      return blob;
    } catch (freshError) {
      captureFilestackDownloadError(freshError, {
        ...sentryContext,
        recovered: false,
        staleError: String(staleError),
      });
      throw freshError;
    }
  }
}

/** Builds a refreshUrl callback that re-mints a DocFile's signed URL, bypassing the Apollo cache. */
export function createDocFileUrlRefresher(
  client: ReturnType<typeof useApolloClient>,
  fileId: UUID
) {
  return async () => {
    const { data } = await client.query<Apollo.GetFileQuery, Apollo.GetFileQueryVariables>({
      fetchPolicy: 'network-only',
      query: Apollo.GetFileDocument,
      variables: { fileId },
    });
    return data?.file?.filestackUrl ?? null;
  };
}

/**
 * Display-only recovery for `<img>` elements rendering signed Filestack URLs. When the image
 * fails to load (typically an expired policy), re-mints the DocFile's signed URL once and swaps
 * it in, so the image recovers without a full page refresh.
 */
export function useRefreshableFilestackImg(
  file: { filestackUrl: string; id: UUID } | null | undefined
): { onError: () => void; src: string | null } {
  const fileId = file?.id ?? null;
  const client = useApolloClient();
  const [refreshedUrlByFileId, setRefreshedUrlByFileId] = useState<Record<string, string>>({});

  const onError = useCallback(() => {
    // Only refresh once per file so a still-broken fresh URL can't loop.
    if (!fileId || refreshedUrlByFileId[fileId]) return;

    void createDocFileUrlRefresher(client, fileId)()
      .then(freshUrl => {
        if (!freshUrl) return;
        setRefreshedUrlByFileId(prev => ({ ...prev, [fileId]: freshUrl }));
      })
      .catch(() => null);
  }, [client, fileId, refreshedUrlByFileId]);
  const refreshedUrl = fileId ? refreshedUrlByFileId[fileId] : null;

  return { onError, src: refreshedUrl ?? file?.filestackUrl ?? null };
}

function captureFilestackDownloadError(error: unknown, extra: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra,
    tags: {
      feature: 'filestack-download',
      reason: error instanceof FilestackDownloadError ? error.reason : 'unknown',
    },
  });
}

async function getResponseBodySnippet(response: Response) {
  try {
    const text = await response.text();
    return text.slice(0, 200);
  } catch {
    return '';
  }
}
